import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ministriesAPI, hubError } from '../lib/api';
import { MemberPicker } from '../components/MemberPicker';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Church, Plus, Edit, Trash2, Loader2, Users, Clock, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const emptyForm = { name: '', description: '', category: '', leader: '', schedule: '', image_url: '' };

export default function MinistriesPage() {
  const { selectedChurchId, canCreate, canUpdate, canDelete } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [membersOf, setMembersOf] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersUnavailable, setMembersUnavailable] = useState(false);
  const [total, setTotal] = useState(0);
  const pg = usePagination(String(selectedChurchId));

  const fetchItems = useCallback(() => {
    setLoading(true);
    ministriesAPI.list(pg.params)
      .then(res => { setItems(res.data?.data || []); setTotal(res.data?.total || 0); })
      .catch(err => toast.error(hubError(err, 'Error al cargar ministerios')))
      .finally(() => setLoading(false));
  }, [pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name || '', description: item.description || '', category: item.category || '',
      leader: item.leader || '', schedule: item.schedule || '', image_url: item.image_url || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    const payload = { ...form, category: form.category || null, leader: form.leader || null, schedule: form.schedule || null, image_url: form.image_url || null };
    try {
      if (editing) { await ministriesAPI.update(editing, payload); toast.success('Ministerio actualizado'); }
      else { await ministriesAPI.create(payload); toast.success('Ministerio creado'); }
      setDialogOpen(false); fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al guardar')); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await ministriesAPI.delete(id); toast.success('Ministerio eliminado'); fetchItems(); }
    catch (err) { toast.error(hubError(err, 'Error al eliminar')); }
  };

  const openMembers = (item) => {
    setMembersOf(item);
    setMembersLoading(true);
    setMembersUnavailable(false);
    ministriesAPI.members(item.id)
      .then(res => setMembers(res.data?.data || []))
      .catch(err => {
        if (err?.response?.status === 404) setMembersUnavailable(true);
        else toast.error(hubError(err, 'Error al cargar integrantes'));
      })
      .finally(() => setMembersLoading(false));
  };

  const removeMember = async (userId) => {
    try {
      await ministriesAPI.removeMember(membersOf.id, userId);
      setMembers(prev => prev.filter(m => (m.id || m.user_id) !== userId));
      toast.success('Integrante removido');
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al remover integrante')); }
  };

  const addMember = async (member) => {
    try {
      await ministriesAPI.addMember(membersOf.id, member.id);
      setMembers(prev => [...prev, member]);
      toast.success(`${member.firstname || 'Miembro'} agregado al ministerio`);
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al agregar integrante')); }
  };

  return (
    <div>
      <TopBar title="Ministerios" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={canCreate('ministries') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={openCreate} data-testid="ministries-create-button"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="ministerios" testId="ministries-pagination"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><Church className="w-12 h-12 text-muted-foreground/40" /><p>No hay ministerios</p>{canCreate('ministries') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={openCreate}>Crear ministerio</Button>}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <Card key={item.id} className="stat-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#1F9D7A]/10 flex items-center justify-center">
                      <Church className="w-5 h-5 text-[#1F9D7A]" />
                    </div>
                    <div className="flex gap-1">
                      {canUpdate('ministries') && <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`ministry-edit-${item.id}`}><Edit className="w-4 h-4" /></Button>}
                      {canDelete('ministries') && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`ministry-delete-${item.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>¿Eliminar ministerio?</AlertDialogTitle><AlertDialogDescription>No se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{item.name}</h3>
                  {item.category && <Badge variant="secondary" className="text-[10px] mb-2">{item.category}</Badge>}
                  {item.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{item.description}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {item.leader && <span>{item.leader}</span>}
                    {item.schedule && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.schedule}</span>}
                  </div>
                  <button
                    onClick={() => openMembers(item)}
                    className="flex items-center gap-1 text-xs text-[#056A84] hover:underline mt-3"
                    data-testid={`ministry-members-${item.id}`}
                  >
                    <Users className="w-3 h-3" /> {item.member_count ?? 0} integrantes
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nuevo'} Ministerio</DialogTitle><DialogDescription className="sr-only">Formulario de ministerio</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ministry-name-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="ministry-description-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Categoría</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="ministry-category-input" /></div>
              <div className="space-y-2"><Label>Líder</Label><Input value={form.leader} onChange={e => setForm({...form, leader: e.target.value})} data-testid="ministry-leader-input" /></div>
            </div>
            <div className="space-y-2"><Label>Horario</Label><Input value={form.schedule} onChange={e => setForm({...form, schedule: e.target.value})} data-testid="ministry-schedule-input" /></div>
            <div className="space-y-2"><Label>Imagen (URL)</Label><Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="ministry-save-button">{saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}{editing ? 'Actualizar' : 'Crear'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!membersOf} onOpenChange={() => setMembersOf(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Integrantes · {membersOf?.name}</DialogTitle>
            <DialogDescription>Miembros asignados a este ministerio</DialogDescription>
          </DialogHeader>
          {membersLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 spinner text-[#056A84]" /></div>
          ) : membersUnavailable ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="ministry-members-unavailable">
              La gestión de integrantes aún no está publicada en el servidor de Tu Iglesia App.
              En cuanto se active, podrás asignar y remover miembros desde aquí.
            </p>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Aún no hay integrantes asignados</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="ministry-members-list">
              {members.map(m => {
                const uid = m.id || m.user_id;
                return (
                  <div key={uid} className="flex items-center justify-between gap-2 p-2 rounded-lg border">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#00CCFF]/10 flex items-center justify-center text-xs font-medium text-[#056A84]">
                        {(m.firstname || 'U')[0]}
                      </div>
                      <p className="text-sm truncate">{m.firstname || ''} {m.lastname || ''}</p>
                    </div>
                    {canUpdate('ministries') && (
                      <Button variant="ghost" size="sm" onClick={() => removeMember(uid)} data-testid={`ministry-member-remove-${uid}`}>
                        <UserMinus className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {canUpdate('ministries') && membersOf && !membersUnavailable && (
            <MemberPicker
              excludeIds={members.map(m => m.id || m.user_id)}
              onAdd={addMember}
              testIdPrefix="ministry"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
